import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Loader2, X } from 'lucide-react';
import { forumsAPI, usersAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { pinScrollTop } from '../lib/pinScrollTop';
import { isAdminRole, isFacilitatorRole, isGodModeRole } from '../utils/roles';
import { MemberBillingSection } from './MemberBillingSection';
import type { AdminMemberRow, ForumListItem, UserRole } from '../types';

const MANAGE_ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'guest', label: 'Guest' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'facilitator', label: 'Facilitator' },
  { value: 'facilitator_admin', label: 'Facilitator + Admin' },
  { value: 'admin', label: 'Admin' },
];

const SNAPSHOT_TYPE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'leader', label: 'Leader' },
  { value: 'nonprofit', label: 'Nonprofit' },
];

function formatDuesAmount(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '';
}

const FACILITATOR_ROLE_OPTIONS: { value: 'member' | 'advisor' | 'guest'; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'guest', label: 'Guest' },
];

function roleChangeNote(from: UserRole, to: UserRole): string | null {
  if (from === to) return null;
  if (to === 'guest' && (from === 'member' || from === 'advisor')) {
    return 'They switch to guest access right away (no Messages, Member Directory, or Grow Goals). Their dues stop after their current billing period.';
  }
  if (from === 'guest' && (to === 'member' || to === 'advisor')) {
    return 'They go through membership onboarding the next time they sign in. Answers they already gave stay filled in; they still confirm dues and the remaining agreements.';
  }
  if ((from === 'member' && to === 'advisor') || (from === 'advisor' && to === 'member')) {
    return `Starting with their next bill, they pay the Forum's ${to} price. A custom dues amount stays as it is.`;
  }
  return null;
}

function trimField(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function localDateInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function daysFromToday(days: number): Date {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
}

// Centered modal for staff member management: contact profile, role,
// snapshot type, forum membership, dues, limit access, billing, and
// (admin) deactivation.
export function ManageMemberPanel({
  member,
  forums,
  selfId,
  canEditRole,
  canViewBilling = false,
  canManageBilling = false,
  onClose,
  onSaved,
}: {
  member: AdminMemberRow;
  forums: ForumListItem[];
  selfId?: number;
  canEditRole: boolean;
  canViewBilling?: boolean;
  canManageBilling?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const originalAmount = formatDuesAmount(
    member.custom_monthly_amount ?? member.monthly_price,
  );
  const [role, setRole] = useState<UserRole>(member.role);
  const [snapshotType, setSnapshotType] = useState(member.snapshot_type);
  const [forumIds, setForumIds] = useState<Set<number>>(
    () => new Set(member.forums.map((f) => f.id)),
  );
  const [limitAccess, setLimitAccess] = useState(member.limit_app_access);
  const [monthlyAmount, setMonthlyAmount] = useState(originalAmount);
  const [firstName, setFirstName] = useState(member.first_name || '');
  const [lastName, setLastName] = useState(member.last_name || '');
  const [email, setEmail] = useState(member.email || '');
  const [cellPhone, setCellPhone] = useState(member.cell_phone || '');
  const [city, setCity] = useState(member.location_city || '');
  const [company, setCompany] = useState(member.business_type || '');
  const [guestExpiresAt, setGuestExpiresAt] = useState(member.guest_expires_at ?? null);
  const [guestUntil, setGuestUntil] = useState(() => localDateInput(daysFromToday(30)));
  const [newRole, setNewRole] = useState<'member' | 'advisor' | 'guest' | ''>('');
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => pinScrollTop(bodyRef.current), [member.id]);

  useEffect(() => {
    setLimitAccess(member.limit_app_access);
    setFirstName(member.first_name || '');
    setLastName(member.last_name || '');
    setEmail(member.email || '');
    setCellPhone(member.cell_phone || '');
    setCity(member.location_city || '');
    setCompany(member.business_type || '');
  }, [
    member.limit_app_access,
    member.first_name,
    member.last_name,
    member.email,
    member.cell_phone,
    member.location_city,
    member.business_type,
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const { user: actor } = useAuth();

  const roleOptions = isGodModeRole(actor?.role)
    ? [...MANAGE_ROLE_OPTIONS, { value: 'god_mode' as UserRole, label: 'Super Admin' }]
    : MANAGE_ROLE_OPTIONS;

  const isSelf = member.id === selfId;
  const originalForumIds = useMemo(() => new Set(member.forums.map((f) => f.id)), [member]);

  const amountDirty = formatDuesAmount(monthlyAmount) !== originalAmount;
  const firstNameDirty = trimField(firstName) !== trimField(member.first_name);
  const lastNameDirty = trimField(lastName) !== trimField(member.last_name);
  const emailDirty = trimField(email).toLowerCase() !== trimField(member.email).toLowerCase();
  const phoneDirty = trimField(cellPhone) !== trimField(member.cell_phone);
  const cityDirty = trimField(city) !== trimField(member.location_city);
  const companyDirty = trimField(company) !== trimField(member.business_type);
  const profileDirty =
    firstNameDirty || lastNameDirty || emailDirty || phoneDirty || cityDirty || companyDirty;
  const dirty =
    role !== member.role ||
    snapshotType !== member.snapshot_type ||
    amountDirty ||
    profileDirty ||
    forumIds.size !== originalForumIds.size ||
    [...forumIds].some((id) => !originalForumIds.has(id));

  const toggleForum = (id: number) => {
    setForumIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (amountDirty && monthlyAmount.trim() !== '') {
      const parsed = Number(monthlyAmount);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError('Enter a valid monthly amount.');
        return;
      }
    }
    const nextEmail = trimField(email);
    if (emailDirty && !nextEmail) {
      setError('Email is required.');
      return;
    }
    if (
      emailDirty &&
      !window.confirm(
        `Change ${member.full_name}'s sign-in email to ${nextEmail}?\n\n` +
          'They will sign in with the new email. We will notify ' +
          `${member.email} and ${nextEmail}.`,
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    setWarning(null);
    try {
      const payload: Parameters<typeof usersAPI.manageMember>[1] = {};
      if (canEditRole && role !== member.role) payload.role = role;
      if (canEditRole && snapshotType !== member.snapshot_type) {
        payload.snapshot_type = snapshotType;
      }
      if (amountDirty) {
        payload.custom_monthly_amount =
          monthlyAmount.trim() === '' ? null : Number(monthlyAmount).toFixed(2);
      }
      if (firstNameDirty) payload.first_name = trimField(firstName);
      if (lastNameDirty) payload.last_name = trimField(lastName);
      if (emailDirty) payload.email = nextEmail;
      if (phoneDirty) payload.cell_phone = trimField(cellPhone);
      if (cityDirty) payload.location_city = trimField(city);
      if (companyDirty) payload.business_type = trimField(company);
      if (Object.keys(payload).length > 0) {
        const result = await usersAPI.manageMember(member.id, payload);
        if (result.stripe_warning) setWarning(result.stripe_warning);
      }
      if (canEditRole) {
        const toAdd = [...forumIds].filter((id) => !originalForumIds.has(id));
        const toRemove = [...originalForumIds].filter((id) => !forumIds.has(id));
        for (const forumId of toAdd) {
          await forumsAPI.addMember(forumId, member.id);
        }
        for (const forumId of toRemove) {
          await forumsAPI.removeMember(forumId, member.id);
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not save the changes. Please try again.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleExtendGuest = async () => {
    if (!guestUntil) return;
    setSaving(true);
    setError(null);
    try {
      const result = await usersAPI.manageMember(member.id, {
        guest_expires_at: new Date(`${guestUntil}T23:59:00`).toISOString(),
      });
      setGuestExpiresAt(result.guest_expires_at ?? null);
      onSaved();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not extend guest access.';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleChangeRole = async () => {
    if (!newRole) return;
    const label = { member: 'a member', advisor: 'an advisor', guest: 'a guest' }[newRole];
    const note = roleChangeNote(member.role, newRole);
    if (!window.confirm(`Make ${member.full_name} ${label}?${note ? `\n\n${note}` : ''}`)) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await usersAPI.manageMember(member.id, { role: newRole });
      onSaved();
      if (updated?.stripe_warning) {
        setWarning(updated.stripe_warning);
        setSaving(false);
        return;
      }
      onClose();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not change their role.';
      setError(detail);
      setSaving(false);
    }
  };

  const guestEnded = guestExpiresAt != null && new Date(guestExpiresAt) <= new Date();
  const canChangeRole =
    !canEditRole &&
    isFacilitatorRole(actor?.role) &&
    FACILITATOR_ROLE_OPTIONS.some((opt) => opt.value === member.role);

  const handleToggleActive = async () => {
    const deactivating = member.is_active;
    if (
      deactivating &&
      !window.confirm(
        `Deactivate ${member.full_name}? They will no longer be able to sign in. ` +
          'You can reactivate them later via "Deactivated only".',
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await usersAPI.manageMember(member.id, { is_active: !member.is_active });
      onSaved();
      onClose();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not update the account. Please try again.';
      setError(detail);
      setSaving(false);
    }
  };

  const handlePurge = async () => {
    const typed = window.prompt(
      `This permanently deletes ${member.full_name} and frees ${member.email} for a new invite. This cannot be undone.\n\nType their email to confirm:`,
    );
    if (typed == null) return;
    if (typed.trim().toLowerCase() !== member.email.toLowerCase()) {
      setError('Email did not match. Permanent delete cancelled.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await usersAPI.purgeMember(member.id);
      onSaved();
      onClose();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not permanently delete this account. Please try again.';
      setError(detail);
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-navy truncate">
              Manage {member.full_name}
            </h3>
            <p className="text-sm text-gray-500 truncate">{member.email}</p>
            {!member.is_active && (
              <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                {member.deletion_requested_at ? 'Deletion requested' : 'Deactivated'}
              </span>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1 text-gray-400 hover:text-navy">
            <X size={20} />
          </button>
        </div>

        <div
          ref={bodyRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-6 py-5 space-y-6 min-w-0"
        >
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          {warning && (
            <div className="bg-yellow-50 text-yellow-700 px-4 py-3 rounded-lg text-sm">
              Saved, but Stripe reported: {warning}
            </div>
          )}
          {member.role === 'guest' && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Guest access</p>
              <p className={`text-sm ${guestEnded ? 'text-red-600' : 'text-gray-600'}`}>
                {guestExpiresAt
                  ? `${guestEnded ? 'Ended' : 'Ends'} ${new Date(guestExpiresAt).toLocaleDateString(undefined, {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}`
                  : 'No end date'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={guestUntil}
                  min={localDateInput(new Date())}
                  onChange={(e) => setGuestUntil(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                  aria-label="Guest access until"
                />
                <button
                  type="button"
                  onClick={() => void handleExtendGuest()}
                  disabled={saving || !guestUntil}
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 transition disabled:opacity-50"
                >
                  Extend guest access
                </button>
              </div>
              <p className="text-xs text-gray-500">
                For returning guests. They can sign in through the end of that
                day and see this month&apos;s Library teaching.
              </p>
            </div>
          )}
          {canChangeRole && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Role:{' '}
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-navy/10 text-navy">
                  {FACILITATOR_ROLE_OPTIONS.find((opt) => opt.value === member.role)?.label}
                </span>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as typeof newRole)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold bg-white"
                  aria-label="Change role to"
                >
                  <option value="" disabled>Change to…</option>
                  {FACILITATOR_ROLE_OPTIONS.filter((opt) => opt.value !== member.role).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void handleChangeRole()}
                  disabled={saving || !newRole}
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 transition disabled:opacity-50"
                >
                  Change role
                </button>
              </div>
              {newRole && (
                <p className="text-xs text-gray-500">{roleChangeNote(member.role, newRole)}</p>
              )}
            </div>
          )}
          {canEditRole && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold bg-white"
                >
                  {roleOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {isSelf && (
                  <p className="text-xs text-gray-500 mt-1">
                    You can't remove your own admin access.
                  </p>
                )}
                {roleChangeNote(member.role, role) && (
                  <p className="text-xs text-gray-500 mt-1">{roleChangeNote(member.role, role)}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Snapshot type</label>
                <select
                  value={snapshotType}
                  onChange={(e) => setSnapshotType(e.target.value as AdminMemberRow['snapshot_type'])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold bg-white"
                >
                  {SNAPSHOT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Controls which Snapshot categories this member fills out.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Forum membership</label>
                {forums.length === 0 ? (
                  <p className="text-sm text-gray-400">No Forums yet.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                    {forums.map((f) => (
                      <label
                        key={f.id}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-off-white/60"
                      >
                        <input
                          type="checkbox"
                          checked={forumIds.has(f.id)}
                          onChange={() => toggleForum(f.id)}
                          className="rounded border-gray-300 text-navy focus:ring-gold"
                        />
                        <span className="truncate">{f.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Profile</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="block text-gray-600 mb-1">First name</span>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={150}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                  aria-label="First name"
                />
              </label>
              <label className="block text-sm">
                <span className="block text-gray-600 mb-1">Last name</span>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={150}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                  aria-label="Last name"
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="block text-gray-600 mb-1">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                aria-label="Email"
              />
              <span className="block text-xs text-gray-500 mt-1">
                Changing this is how they sign in. We email the old and new
                address.
              </span>
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="block text-gray-600 mb-1">Cell phone</span>
                <input
                  type="tel"
                  value={cellPhone}
                  onChange={(e) => setCellPhone(e.target.value)}
                  maxLength={30}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                  aria-label="Cell phone"
                />
              </label>
              <label className="block text-sm">
                <span className="block text-gray-600 mb-1">City</span>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  maxLength={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                  aria-label="City"
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="block text-gray-600 mb-1">Company</span>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                aria-label="Company"
              />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly dues
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={monthlyAmount}
                onChange={(e) => setMonthlyAmount(e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                aria-label="Monthly dues"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              What they pay each month. $0 means no charge. Any amount above
              $0 bills through Stripe. Clear the field and save to reset to
              the Forum&apos;s standard rate.
            </p>
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={limitAccess}
                disabled={saving}
                onChange={async (e) => {
                  const limited = e.target.checked;
                  setSaving(true);
                  setError(null);
                  try {
                    await usersAPI.manageMember(member.id, { limit_app_access: limited });
                    setLimitAccess(limited);
                    onSaved();
                  } catch (err) {
                    const detail =
                      (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                      'Could not update app access.';
                    setError(detail);
                  } finally {
                    setSaving(false);
                  }
                }}
                className="mt-0.5 rounded border-gray-300 text-navy focus:ring-gold"
              />
              <span>
                Limit app access
                <span className="block text-xs font-normal text-gray-500 mt-0.5">
                  Guest-lock Messages, the directory, and other member areas.
                  Independent of whether dues are paused.
                </span>
              </span>
            </label>
          </div>

          {canViewBilling && (
            <MemberBillingSection
              memberId={member.id}
              role={member.role}
              monthlyAmount={member.custom_monthly_amount ?? member.monthly_price}
              onChanged={onSaved}
              canManage={canManageBilling}
            />
          )}

          {canEditRole && (
            <div className="border-t border-gray-100 pt-4">
              <button
                onClick={handleToggleActive}
                disabled={saving || (isSelf && member.is_active)}
                className={`w-full py-2.5 rounded-lg font-medium border transition disabled:opacity-50 ${
                  member.is_active
                    ? 'border-red-200 text-red-600 hover:bg-red-50'
                    : 'border-green-200 text-green-700 hover:bg-green-50'
                }`}
              >
                {member.is_active ? 'Deactivate account' : 'Reactivate account'}
              </button>
              {isSelf && member.is_active && (
                <p className="text-xs text-gray-500 mt-1">You can't deactivate your own account.</p>
              )}
              {!isSelf && (
                <button
                  type="button"
                  onClick={handlePurge}
                  disabled={saving}
                  className="mt-3 w-full py-2.5 rounded-lg font-medium border border-red-600 text-red-700 hover:bg-red-50 transition disabled:opacity-50"
                >
                  Delete permanently
                </button>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Deactivate signs them out and keeps the email reserved so you can restore them.
                Delete permanently erases the account now so that email can be invited again.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex-1 bg-navy text-white py-2.5 rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageShell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl p-8 text-center">
        {children}
      </div>
    </div>
  );
}

// Loads the member row by id, then opens the same Manage sheet used on
// Admin → Members. Used from Forum roster and Forum member Details.
export function ManageMemberById({
  memberId,
  onClose,
  onSaved,
}: {
  memberId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const isFacilitator = isFacilitatorRole(user?.role);
  const [member, setMember] = useState<AdminMemberRow | null>(null);
  const [forums, setForums] = useState<ForumListItem[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    Promise.all([
      usersAPI.getAdminMembers({
        id: memberId,
        include_inactive: '1',
      }),
      isAdmin ? forumsAPI.getAll() : forumsAPI.getMyForums(),
    ])
      .then(([rows, forumList]) => {
        if (!active) return;
        const row = rows.find((item) => item.id === memberId) ?? null;
        setMember(row);
        setForums(forumList);
        if (!row) setError('Could not open this member.');
      })
      .catch(() => {
        if (active) setError('Could not open this member.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [memberId, isAdmin]);

  if (loading) {
    return (
      <ManageShell onClose={onClose}>
        <Loader2 className="animate-spin text-navy mx-auto" size={28} />
        <p className="sr-only">Loading member</p>
      </ManageShell>
    );
  }

  if (!member) {
    return (
      <ManageShell onClose={onClose}>
        <p className="text-navy font-medium">{error || 'Could not open this member.'}</p>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
      </ManageShell>
    );
  }

  return (
    <ManageMemberPanel
      member={member}
      forums={forums}
      selfId={user?.id}
      canEditRole={isAdmin}
      canViewBilling={isAdmin || isFacilitator}
      canManageBilling={isAdmin || isFacilitator}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}
